<template>
  <DxPopup
    :visible="visible"
    :width="'85vw'"
    :height="'90vh'"
    :show-title="true"
    :title="title"
    :show-close-button="true"
    :hide-on-outside-click="false"
    :drag-enabled="false"
    :resize-enabled="false"
    @hiding="close"
  >
    <div ref="stageWrapper" class="photo-slider-dialog" :class="{ 'is-fullscreen': isFullscreen }">
      <div class="photo-slider-stage">
        <button
          class="photo-slider-control"
          type="button"
          :disabled="!hasPrevious"
          :aria-label="previousLabel"
          @click="previous"
        >
          <i class="fa fa-chevron-left"></i>
        </button>

        <div
          class="photo-slider-image-holder"
          :class="{ 'is-zoomed': isZoomed }"
          @wheel.prevent="zoomWithWheel"
          @mousedown="startPan"
          @mousemove="panImage"
          @mouseup="stopPan"
          @mouseleave="stopPan"
        >
          <button
            class="photo-slider-fullscreen-toggle"
            type="button"
            :aria-label="fullscreenLabel"
            @click="toggleFullscreen"
          >
            <i class="fa" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
          </button>

          <div class="photo-slider-zoom-controls">
            <button
              class="photo-slider-zoom-button"
              type="button"
              :disabled="zoomLevel >= maxZoom"
              aria-label="Zoom in"
              @click.stop="zoomIn"
            >
              <i class="fa fa-plus"></i>
            </button>
            <button
              class="photo-slider-zoom-button"
              type="button"
              :disabled="zoomLevel <= minZoom"
              aria-label="Zoom out"
              @click.stop="zoomOut"
            >
              <i class="fa fa-minus"></i>
            </button>
            <button
              class="photo-slider-zoom-button"
              type="button"
              :disabled="!isZoomed"
              aria-label="Reset zoom"
              @click.stop="resetZoom"
            >
              <i class="fa fa-search"></i>
            </button>
          </div>

          <img
            v-if="currentImage"
            class="photo-slider-image"
            :src="currentImage.image_url"
            :alt="title"
            :style="imageStyle"
            draggable="false"
          >
        </div>

        <button
          class="photo-slider-control"
          type="button"
          :disabled="!hasNext"
          :aria-label="nextLabel"
          @click="next"
        >
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>

      <div class="photo-slider-thumbnails">
        <button
          v-for="(image, index) in images"
          :key="image.uuid || image.image_url || index"
          class="photo-slider-thumbnail"
          :class="{ 'is-active': index === currentIndex }"
          type="button"
          :aria-label="title + ' ' + (index + 1)"
          @click="selectImage(index)"
        >
          <img :src="image.image_url" :alt="title + ' ' + (index + 1)">
        </button>
      </div>
    </div>
  </DxPopup>
</template>

<script>
import DxPopup from "devextreme-vue/popup";

export default {
  name: "PhotoSliderDialog",
  components: {
    DxPopup,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    images: {
      type: Array,
      default: () => [],
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: "Photos",
    },
    previousLabel: {
      type: String,
      default: "Previous photo",
    },
    nextLabel: {
      type: String,
      default: "Next photo",
    },
    fullscreenLabel: {
      type: String,
      default: "Toggle fullscreen",
    },
  },
  data() {
    return {
      currentIndex: 0,
      isFullscreen: false,
      zoomLevel: 1,
      offsetX: 0,
      offsetY: 0,
      isPanning: false,
      panStartX: 0,
      panStartY: 0,
    };
  },
  computed: {
    currentImage() {
      return this.images[this.currentIndex] || null;
    },
    hasPrevious() {
      return this.currentIndex > 0;
    },
    hasNext() {
      return this.currentIndex < this.images.length - 1;
    },
    minZoom() {
      return 1;
    },
    maxZoom() {
      return 4;
    },
    isZoomed() {
      return this.zoomLevel > this.minZoom;
    },
    imageStyle() {
      return {
        transform: `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.zoomLevel})`,
      };
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        if (isVisible) {
          this.currentIndex = this.getValidIndex(this.initialIndex);
          window.addEventListener("keydown", this.handleKeydown);
          document.addEventListener("fullscreenchange", this.handleFullscreenChange);
          return;
        }

        window.removeEventListener("keydown", this.handleKeydown);
        document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
        this.exitFullscreen();
      },
    },
    initialIndex(index) {
      if (this.visible) {
        this.currentIndex = this.getValidIndex(index);
      }
    },
    images() {
      this.currentIndex = this.getValidIndex(this.currentIndex);
    },
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
  },
  methods: {
    getValidIndex(index) {
      if (!this.images.length) {
        return 0;
      }

      return Math.max(0, Math.min(index, this.images.length - 1));
    },
    selectImage(index) {
      this.currentIndex = this.getValidIndex(index);
      this.resetZoom();
    },
    previous() {
      if (this.hasPrevious) {
        this.currentIndex -= 1;
        this.resetZoom();
      }
    },
    next() {
      if (this.hasNext) {
        this.currentIndex += 1;
        this.resetZoom();
      }
    },
    zoomIn() {
      this.setZoom(this.zoomLevel + 0.5);
    },
    zoomOut() {
      this.setZoom(this.zoomLevel - 0.5);
    },
    zoomWithWheel(event) {
      if (event.deltaY < 0) {
        this.zoomIn();
        return;
      }

      this.zoomOut();
    },
    setZoom(level) {
      this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, level));
      if (this.zoomLevel === this.minZoom) {
        this.offsetX = 0;
        this.offsetY = 0;
      }
    },
    resetZoom() {
      this.zoomLevel = this.minZoom;
      this.offsetX = 0;
      this.offsetY = 0;
      this.stopPan();
    },
    startPan(event) {
      if (!this.isZoomed || event.button !== 0) {
        return;
      }

      this.isPanning = true;
      this.panStartX = event.clientX - this.offsetX;
      this.panStartY = event.clientY - this.offsetY;
    },
    panImage(event) {
      if (!this.isPanning) {
        return;
      }

      this.offsetX = event.clientX - this.panStartX;
      this.offsetY = event.clientY - this.panStartY;
    },
    stopPan() {
      this.isPanning = false;
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        if (this.isFullscreen) {
          this.exitFullscreen();
          return;
        }

        this.close();
        return;
      }

      if (event.key === "ArrowLeft") {
        this.previous();
      }

      if (event.key === "ArrowRight") {
        this.next();
      }
    },
    toggleFullscreen() {
      if (this.isFullscreen) {
        this.exitFullscreen();
        return;
      }

      const element = this.$refs.stageWrapper;
      if (element && element.requestFullscreen) {
        element.requestFullscreen().catch(() => {});
      }
    },
    exitFullscreen() {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
    close() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.photo-slider-dialog {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.photo-slider-dialog.is-fullscreen {
  padding: 16px;
  background: #000000;
}

.photo-slider-stage {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) 48px;
  align-items: center;
  flex: 1;
  min-height: 0;
  gap: 12px;
}

.photo-slider-image-holder {
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  cursor: default;
}

.photo-slider-image-holder.is-zoomed {
  cursor: grab;
}

.photo-slider-image-holder.is-zoomed:active {
  cursor: grabbing;
}

.photo-slider-image {
  width: 100%;
  height: 100%;
  min-height: 0;
  object-fit: contain;
  user-select: none;
  transition: transform 120ms ease-out;
}

.photo-slider-image-holder.is-zoomed .photo-slider-image {
  transition: none;
}

.photo-slider-fullscreen-toggle {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  width: 36px;
  height: 36px;
  border: 1px solid #c7d0d8;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.9);
  color: #40515f;
  cursor: pointer;
}

.is-fullscreen .photo-slider-fullscreen-toggle {
  background: rgba(0, 0, 0, 0.6);
  border-color: rgba(255, 255, 255, 0.4);
  color: #ffffff;
}

.photo-slider-zoom-controls {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  display: flex;
  gap: 4px;
}

.photo-slider-zoom-button {
  width: 32px;
  height: 32px;
  border: 1px solid #c7d0d8;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.9);
  color: #40515f;
  cursor: pointer;
}

.photo-slider-zoom-button:disabled {
  cursor: default;
  opacity: 0.45;
}

.is-fullscreen .photo-slider-zoom-button {
  background: rgba(0, 0, 0, 0.6);
  border-color: rgba(255, 255, 255, 0.4);
  color: #ffffff;
}

.photo-slider-control {
  width: 40px;
  height: 40px;
  border: 1px solid #c7d0d8;
  border-radius: 4px;
  background: #ffffff;
  color: #40515f;
  cursor: pointer;
}

.photo-slider-control:disabled {
  cursor: default;
  opacity: 0.4;
}

.photo-slider-thumbnails {
  display: flex;
  justify-content: center;
  gap: 8px;
  overflow-x: auto;
  padding: 12px 4px 0;
}

.photo-slider-thumbnail {
  flex: 0 0 72px;
  width: 72px;
  height: 56px;
  padding: 0;
  overflow: hidden;
  border: 2px solid transparent;
  border-radius: 4px;
  background: #becad7;
  cursor: pointer;
}

.photo-slider-thumbnail.is-active {
  border-color: #5a8b4d;
}

.photo-slider-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 576px) {
  .photo-slider-stage {
    grid-template-columns: 36px minmax(0, 1fr) 36px;
    gap: 6px;
  }

  .photo-slider-control {
    width: 32px;
    height: 32px;
  }
}
</style>